import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ComparisonFilters,
  FilterOption,
  GroupByDimension,
} from '../../services/comparisonDashboardService';
import { comparisonDashboardService } from '../../services/comparisonDashboardService';
import { getDefaultDateRange } from '../../pages/reports/ComparisonDashboardPage';

interface ComparisonFilterPanelProps {
  filters: ComparisonFilters;
  onFiltersChange: (filters: ComparisonFilters) => void;
  loading?: boolean;
}

type DimensionKey =
  | 'brands'
  | 'categories'
  | 'products'
  | 'customers'
  | 'sales_reps'
  | 'territories';

const DIMENSIONS: { key: DimensionKey; apiDimension: GroupByDimension; label: string }[] = [
  { key: 'brands', apiDimension: 'brand', label: 'Brand' },
  { key: 'categories', apiDimension: 'category', label: 'Category' },
  { key: 'products', apiDimension: 'product', label: 'Product' },
  { key: 'customers', apiDimension: 'customer', label: 'Customer' },
  { key: 'sales_reps', apiDimension: 'sales_rep', label: 'Sales Rep' },
  { key: 'territories', apiDimension: 'territory', label: 'Territory' },
];

interface SelectedItem {
  id: string;
  name: string;
}

interface DimensionSearchState {
  search: string;
  results: FilterOption[];
  loading: boolean;
  open: boolean;
}

const ComparisonFilterPanel: React.FC<ComparisonFilterPanelProps> = ({
  filters,
  onFiltersChange,
  loading = false,
}) => {
  const [selectedItems, setSelectedItems] = useState<Record<DimensionKey, SelectedItem[]>>(() => {
    const initial: Record<DimensionKey, SelectedItem[]> = {
      brands: [],
      categories: [],
      products: [],
      customers: [],
      sales_reps: [],
      territories: [],
    };
    for (const dim of DIMENSIONS) {
      const ids = filters[dim.key];
      if (ids?.length) {
        initial[dim.key] = ids.map((id) => ({ id, name: id }));
      }
    }
    return initial;
  });

  const [searchStates, setSearchStates] = useState<Record<DimensionKey, DimensionSearchState>>(
    () => {
      const initial: Record<string, DimensionSearchState> = {};
      for (const dim of DIMENSIONS) {
        initial[dim.key] = { search: '', results: [], loading: false, open: false };
      }
      return initial as Record<DimensionKey, DimensionSearchState>;
    }
  );

  const defaults = getDefaultDateRange();
  const [startDate, setStartDate] = useState(filters.start_date || defaults.start_date);
  const [endDate, setEndDate] = useState(filters.end_date || defaults.end_date);

  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const dimsToResolve = DIMENSIONS.filter((dim) => filters[dim.key]?.length);
    if (!dimsToResolve.length) return;

    Promise.all(
      dimsToResolve.map(async (dim) => {
        const ids = filters[dim.key]!;
        const results = await Promise.all(
          ids.map((id) =>
            comparisonDashboardService.getFilterOptions(dim.apiDimension, id).then(
              (opts) => {
                const match = opts.find((o) => o.id === id);
                return { id, name: match?.name || id };
              },
              () => ({ id, name: id })
            )
          )
        );
        return { key: dim.key, items: results };
      })
    ).then((resolved) => {
      setSelectedItems((prev) => {
        const next = { ...prev };
        for (const { key, items } of resolved) {
          next[key] = items;
        }
        return next;
      });
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      setSearchStates((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const dim of DIMENSIONS) {
          const ref = dropdownRefs.current[dim.key];
          if (ref && !ref.contains(event.target as Node) && prev[dim.key].open) {
            next[dim.key] = { ...prev[dim.key], open: false };
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(
    async (dimKey: DimensionKey, apiDim: GroupByDimension, term: string) => {
      if (!term.trim()) {
        setSearchStates((prev) => ({
          ...prev,
          [dimKey]: { ...prev[dimKey], results: [], loading: false },
        }));
        return;
      }

      setSearchStates((prev) => ({
        ...prev,
        [dimKey]: { ...prev[dimKey], loading: true },
      }));

      try {
        const results = await comparisonDashboardService.getFilterOptions(apiDim, term);
        setSearchStates((prev) => ({
          ...prev,
          [dimKey]: { ...prev[dimKey], results, loading: false },
        }));
      } catch {
        setSearchStates((prev) => ({
          ...prev,
          [dimKey]: { ...prev[dimKey], results: [], loading: false },
        }));
      }
    },
    []
  );

  const handleSearchChange = useCallback(
    (dimKey: DimensionKey, apiDim: GroupByDimension, value: string) => {
      setSearchStates((prev) => ({
        ...prev,
        [dimKey]: { ...prev[dimKey], search: value, open: true },
      }));

      if (debounceTimers.current[dimKey]) {
        clearTimeout(debounceTimers.current[dimKey]);
      }
      debounceTimers.current[dimKey] = setTimeout(() => {
        doSearch(dimKey, apiDim, value);
      }, 300);
    },
    [doSearch]
  );

  const buildNewFilters = useCallback(
    (
      newSelected: Record<DimensionKey, SelectedItem[]>,
      sDate: string,
      eDate: string
    ): ComparisonFilters => {
      const f: ComparisonFilters = {};
      for (const dim of DIMENSIONS) {
        const items = newSelected[dim.key];
        if (items.length > 0) {
          (f as any)[dim.key] = items.map((i) => i.id);
        }
      }
      if (sDate) f.start_date = sDate;
      if (eDate) f.end_date = eDate;
      return f;
    },
    []
  );

  const handleSelectItem = useCallback(
    (dimKey: DimensionKey, item: FilterOption) => {
      let newSelected: Record<DimensionKey, SelectedItem[]> | null = null;
      setSelectedItems((prev) => {
        if (prev[dimKey].some((s) => s.id === item.id)) return prev;
        newSelected = {
          ...prev,
          [dimKey]: [...prev[dimKey], { id: item.id, name: item.name }],
        };
        return newSelected;
      });
      setSearchStates((prev) => ({
        ...prev,
        [dimKey]: { ...prev[dimKey], search: '', results: [], open: false },
      }));
      if (newSelected) {
        onFiltersChange(buildNewFilters(newSelected, startDate, endDate));
      }
    },
    [onFiltersChange, buildNewFilters, startDate, endDate]
  );

  const handleRemoveItem = useCallback(
    (dimKey: DimensionKey, itemId: string) => {
      let newSelected: Record<DimensionKey, SelectedItem[]> | null = null;
      setSelectedItems((prev) => {
        newSelected = {
          ...prev,
          [dimKey]: prev[dimKey].filter((s) => s.id !== itemId),
        };
        return newSelected;
      });
      if (newSelected) {
        onFiltersChange(buildNewFilters(newSelected, startDate, endDate));
      }
    },
    [onFiltersChange, buildNewFilters, startDate, endDate]
  );

  const handleDateChange = useCallback(
    (field: 'start' | 'end', value: string) => {
      if (field === 'start') {
        setStartDate(value);
        onFiltersChange(buildNewFilters(selectedItems, value, endDate));
      } else {
        setEndDate(value);
        onFiltersChange(buildNewFilters(selectedItems, startDate, value));
      }
    },
    [onFiltersChange, buildNewFilters, selectedItems, startDate, endDate]
  );

  const handleClearAll = useCallback(() => {
    const empty: Record<DimensionKey, SelectedItem[]> = {
      brands: [],
      categories: [],
      products: [],
      customers: [],
      sales_reps: [],
      territories: [],
    };
    const d = getDefaultDateRange();
    setSelectedItems(empty);
    setStartDate(d.start_date);
    setEndDate(d.end_date);
    onFiltersChange({ start_date: d.start_date, end_date: d.end_date });
  }, [onFiltersChange]);

  const hasAnyFilters =
    Object.values(selectedItems).some((arr) => arr.length > 0) || startDate || endDate;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Filters</h3>
        {hasAnyFilters && (
          <button onClick={handleClearAll} style={styles.clearBtn} disabled={loading}>
            Clear All
          </button>
        )}
      </div>

      <div style={styles.dateRow}>
        <div style={styles.dateField}>
          <label style={styles.label}>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleDateChange('start', e.target.value)}
            style={styles.dateInput}
          />
        </div>
        <div style={styles.dateField}>
          <label style={styles.label}>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => handleDateChange('end', e.target.value)}
            style={styles.dateInput}
          />
        </div>
      </div>

      <div style={styles.dimensionsGrid}>
        {DIMENSIONS.map((dim) => {
          const state = searchStates[dim.key];
          const items = selectedItems[dim.key];

          return (
            <div
              key={dim.key}
              style={styles.dimBlock}
              ref={(el) => {
                dropdownRefs.current[dim.key] = el;
              }}
            >
              <label style={styles.label}>{dim.label}</label>
              <div style={styles.searchWrapper}>
                <input
                  type="text"
                  value={state.search}
                  onChange={(e) => handleSearchChange(dim.key, dim.apiDimension, e.target.value)}
                  onFocus={() =>
                    setSearchStates((prev) => ({
                      ...prev,
                      [dim.key]: { ...prev[dim.key], open: true },
                    }))
                  }
                  placeholder={`Search ${dim.label.toLowerCase()}...`}
                  style={styles.searchInput}
                />
                {state.loading && <span style={styles.spinner} />}
                {state.open && state.results.length > 0 && (
                  <div style={styles.dropdown}>
                    {state.results
                      .filter((r) => !items.some((s) => s.id === r.id))
                      .map((opt) => (
                        <div
                          key={opt.id}
                          onClick={() => handleSelectItem(dim.key, opt)}
                          style={styles.dropdownItem}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f0f4ff';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLDivElement).style.backgroundColor = 'white';
                          }}
                        >
                          {opt.name}
                          {opt.id !== opt.name && <span style={styles.optCode}>{opt.id}</span>}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {items.length > 0 && (
                <div style={styles.chipsRow}>
                  {items.map((item) => (
                    <span
                      key={item.id}
                      style={styles.chip}
                      title={item.id !== item.name ? item.id : undefined}
                    >
                      {item.name}
                      {item.id !== item.name && <span style={styles.chipCode}>{item.id}</span>}
                      <button
                        onClick={() => handleRemoveItem(dim.key, item.id)}
                        style={styles.chipX}
                        title="Remove"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1rem 1.25rem',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1f2937',
  },
  clearBtn: {
    padding: '0.3rem 0.75rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  dateRow: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  dateField: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  label: {
    display: 'block',
    marginBottom: '0.3rem',
    fontWeight: 500,
    fontSize: '0.85rem',
    color: '#374151',
  },
  dateInput: {
    padding: '0.4rem 0.5rem',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  dimensionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '0.75rem',
  },
  dimBlock: {
    position: 'relative' as const,
  },
  searchWrapper: {
    position: 'relative' as const,
  },
  searchInput: {
    width: '100%',
    padding: '0.4rem 0.5rem',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  spinner: {
    position: 'absolute' as const,
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '14px',
    height: '14px',
    border: '2px solid #ddd',
    borderTopColor: '#007bff',
    borderRadius: '50%',
    animation: 'comparison-spin 0.6s linear infinite',
  },
  dropdown: {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '2px',
    backgroundColor: 'white',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    maxHeight: '180px',
    overflowY: 'auto' as const,
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  dropdownItem: {
    padding: '0.4rem 0.6rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  optCode: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    marginLeft: '0.5rem',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px',
    marginTop: '4px',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    backgroundColor: '#e0e7ff',
    color: '#3730a3',
    borderRadius: '12px',
    fontSize: '0.78rem',
    fontWeight: 500,
  },
  chipCode: {
    color: '#6366f1',
    fontSize: '0.72rem',
    opacity: 0.7,
  },
  chipX: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '1rem',
    lineHeight: 1,
    padding: 0,
    fontWeight: 700,
  },
};

export default React.memo(ComparisonFilterPanel);
